import { TCandidate } from '../../../../Candidates/types';
import { Currency } from './constants/currencies';

export interface IntelliHRState {
  candidate: TCandidate | null;
  errors: Record<string, string>;
  preferredName: string;
  gender: [string, string] | null;
  dateOfBirth: Date | null;
  dateError: string;
  personalEmail: boolean;
  personalPhone: boolean;
  title: string | null;
  emergencyContact: {
    name: string;
    relationship: string;
    phone: string;
    email: string;
  };
  attachments: { id: number; file_name: string; type: string; checked: boolean }[];
  personCustomFieldAnswers: Record<string, { value: string | string[]; type: string }>;
  jobCustomFieldAnswers: Record<string, { value: string | string[]; type: string }>;
  remunerationCustomFieldAnswers: Record<string, { value: string | string[]; type: string }>;
  jobStartDate: Date | null;
  jobEndDate: Date | null;
  jobStartDateError: string;
  jobEndDateError: string;
  probationEndDate: Date | null;
  probationEndDateError: string;
  positionTitle: string | null;
  businessEntity: { id: number; name: string } | null;
  businessUnit: { id: number; name: string } | null;
  location: { id: number; name: string } | null;
  supervisor: { id: number; name: string } | null;
  employmentCondition: { id: number; name: string } | null;
  payGrade: { id: number; name: string } | null;
  workClass: { id: number; name: string } | null;
  fte: string;
  payCycle: string | null;
  hoursPerCycle: string;
  additionToBase: Array<{
    name: string;
    type: string;
    value: { value: string; currency: Currency };
  }>;
  additionToTotal: { name: string; type: string; value: { value: string; currency: Currency } }[];
  breakdowns: { name: string; type: string; value: { value: string; currency: Currency } }[];
  currency: Currency;
  salary: string;
  hasEndDate: boolean;
  probationIsNotApplicable: boolean;
  useDefaultProbationLength: boolean;
  sameAsCompanyStartDate: boolean;
  noSupervisor: boolean;
  noFTE: boolean;
  remunerationScheduleType: string;
}

export type IntelliHRAction =
  | { type: 'SET_CANDIDATE'; payload: TCandidate | null }
  | { type: 'SET_ERRORS'; payload: Record<string, string> }
  | { type: 'SET_PREFERRED_NAME'; payload: string }
  | { type: 'SET_GENDER'; payload: [string, string] | null }
  | { type: 'SET_DATE_OF_BIRTH'; payload: Date | null }
  | { type: 'SET_DATE_ERROR'; payload: string }
  | { type: 'SET_PERSONAL_EMAIL'; payload: boolean }
  | { type: 'SET_PERSONAL_PHONE'; payload: boolean }
  | { type: 'SET_TITLE'; payload: string | null }
  | { type: 'SET_EMERGENCY_CONTACT'; payload: Partial<IntelliHRState['emergencyContact']> }
  | {
      type: 'SET_ATTACHMENTS';
      payload: { id: number; file_name: string; type: string; checked: boolean }[];
    }
  | {
      type: 'SET_PERSON_CUSTOM_FIELD_ANSWER';
      payload: {
        id: string;
        answer: string | string[];
        type: string;
      };
    }
  | {
      type: 'SET_JOB_CUSTOM_FIELD_ANSWER';
      payload: {
        id: string;
        answer: string | string[];
        type: string;
      };
    }
  | {
      type: 'SET_REMUNERATION_CUSTOM_FIELD_ANSWER';
      payload: {
        id: string;
        answer: string | string[];
        type: string;
      };
    }
  | { type: 'SET_COMPANY_START_DATE'; payload: Date | null }
  | { type: 'SET_COMPANY_END_DATE'; payload: Date | null }
  | { type: 'SET_COMPANY_START_DATE_ERROR'; payload: string }
  | { type: 'SET_COMPANY_END_DATE_ERROR'; payload: string }
  | { type: 'SET_PROBATION_END_DATE'; payload: Date | null }
  | { type: 'SET_PROBATION_END_DATE_ERROR'; payload: string }
  | { type: 'SET_POSITION_TITLE'; payload: string | null }
  | { type: 'SET_BUSINESS_ENTITY'; payload: { id: number; name: string } | null }
  | { type: 'SET_BUSINESS_UNIT'; payload: { id: number; name: string } | null }
  | { type: 'SET_LOCATION'; payload: { id: number; name: string } | null }
  | { type: 'SET_SUPERVISOR'; payload: { id: number; name: string } | null }
  | { type: 'SET_EMPLOYMENT_CONDITION'; payload: { id: number; name: string } | null }
  | { type: 'SET_PAY_GRADE'; payload: { id: number; name: string } | null }
  | { type: 'SET_WORK_CLASS'; payload: { id: number; name: string } | null }
  | { type: 'SET_FTE'; payload: string }
  | { type: 'SET_PAY_CYCLE'; payload: string | null }
  | { type: 'SET_HOURS_PER_CYCLE'; payload: string }
  | {
      type: 'SET_ADDITION_TO_BASE';
      payload: Array<{ name: string; type: string; value: { value: string; currency: Currency } }>;
    }
  | {
      type: 'SET_ADDITION_TO_TOTAL';
      payload: { name: string; type: string; value: { value: string; currency: Currency } }[];
    }
  | {
      type: 'SET_BREAKDOWNS';
      payload: { name: string; type: string; value: { value: string; currency: Currency } }[];
    }
  | { type: 'SET_CURRENCY'; payload: Currency }
  | { type: 'SET_SALARY'; payload: string }
  | { type: 'SET_HAS_END_DATE'; payload: boolean }
  | { type: 'SET_SAME_AS_COMPANY_START_DATE'; payload: boolean }
  | { type: 'SET_NO_SUPERVISOR'; payload: boolean }
  | { type: 'SET_NO_FTE'; payload: boolean }
  | { type: 'SET_REMUNERATION_SCHEDULE_TYPE'; payload: string }
  | { type: 'SET_PROBATION_IS_NOT_APPLICABLE'; payload: boolean }
  | { type: 'SET_USE_DEFAULT_PROBATION_LENGTH'; payload: boolean };

export const initialState: IntelliHRState = {
  candidate: null,
  errors: {},
  preferredName: '',
  gender: null,
  dateOfBirth: null,
  dateError: '',
  personalEmail: false,
  personalPhone: false,
  title: null,
  emergencyContact: {
    name: '',
    relationship: '',
    phone: '',
    email: ''
  },
  attachments: [],
  personCustomFieldAnswers: {},
  jobCustomFieldAnswers: {},
  remunerationCustomFieldAnswers: {},
  jobStartDate: null,
  jobEndDate: null,
  jobStartDateError: '',
  jobEndDateError: '',
  probationEndDate: null,
  probationEndDateError: '',
  positionTitle: null,
  businessEntity: null,
  businessUnit: null,
  location: null,
  supervisor: null,
  employmentCondition: null,
  payGrade: null,
  workClass: null,
  fte: '',
  payCycle: null,
  hoursPerCycle: '',
  additionToBase: [],
  additionToTotal: [],
  breakdowns: [],
  currency: { code: 'AUD', name: 'Australian Dollar', symbol: '$' },
  salary: '',
  hasEndDate: false,
  sameAsCompanyStartDate: false,
  noSupervisor: false,
  noFTE: false,
  remunerationScheduleType: 'Annual Salary',
  probationIsNotApplicable: false,
  useDefaultProbationLength: false
};

export function intelliHRReducer(state: IntelliHRState, action: IntelliHRAction): IntelliHRState {
  switch (action.type) {
    case 'SET_CANDIDATE':
      return { ...state, candidate: action.payload };
    case 'SET_ERRORS':
      return { ...state, errors: action.payload };
    case 'SET_PREFERRED_NAME':
      return { ...state, preferredName: action.payload };
    case 'SET_GENDER':
      return { ...state, gender: action.payload };
    case 'SET_DATE_OF_BIRTH':
      return { ...state, dateOfBirth: action.payload };
    case 'SET_DATE_ERROR':
      return { ...state, dateError: action.payload };
    case 'SET_PERSONAL_EMAIL':
      return { ...state, personalEmail: action.payload };
    case 'SET_PERSONAL_PHONE':
      return { ...state, personalPhone: action.payload };
    case 'SET_TITLE':
      return { ...state, title: action.payload };
    case 'SET_EMERGENCY_CONTACT':
      return { ...state, emergencyContact: { ...state.emergencyContact, ...action.payload } };
    case 'SET_ATTACHMENTS':
      return { ...state, attachments: action.payload };
    case 'SET_PERSON_CUSTOM_FIELD_ANSWER':
      return {
        ...state,
        personCustomFieldAnswers: {
          ...state.personCustomFieldAnswers,
          [action.payload.id]: { value: action.payload.answer, type: action.payload.type }
        }
      };
    case 'SET_JOB_CUSTOM_FIELD_ANSWER':
      return {
        ...state,
        jobCustomFieldAnswers: {
          ...state.jobCustomFieldAnswers,
          [action.payload.id]: { value: action.payload.answer, type: action.payload.type }
        }
      };
    case 'SET_REMUNERATION_CUSTOM_FIELD_ANSWER':
      return {
        ...state,
        remunerationCustomFieldAnswers: {
          ...state.remunerationCustomFieldAnswers,
          [action.payload.id]: { value: action.payload.answer, type: action.payload.type }
        }
      };
    case 'SET_COMPANY_START_DATE':
      return { ...state, jobStartDate: action.payload };
    case 'SET_COMPANY_END_DATE':
      return { ...state, jobEndDate: action.payload };
    case 'SET_COMPANY_START_DATE_ERROR':
      return { ...state, jobStartDateError: action.payload };
    case 'SET_COMPANY_END_DATE_ERROR':
      return { ...state, jobEndDateError: action.payload };
    case 'SET_PROBATION_END_DATE':
      return { ...state, probationEndDate: action.payload };
    case 'SET_PROBATION_END_DATE_ERROR':
      return { ...state, probationEndDateError: action.payload };
    case 'SET_POSITION_TITLE':
      return { ...state, positionTitle: action.payload };
    case 'SET_BUSINESS_ENTITY':
      return { ...state, businessEntity: action.payload };
    case 'SET_BUSINESS_UNIT':
      return { ...state, businessUnit: action.payload };
    case 'SET_LOCATION':
      return { ...state, location: action.payload };
    case 'SET_SUPERVISOR':
      return { ...state, supervisor: action.payload };
    case 'SET_EMPLOYMENT_CONDITION':
      return { ...state, employmentCondition: action.payload };
    case 'SET_PAY_GRADE':
      return { ...state, payGrade: action.payload };
    case 'SET_WORK_CLASS':
      return { ...state, workClass: action.payload };
    case 'SET_FTE':
      return { ...state, fte: action.payload };
    case 'SET_PAY_CYCLE':
      return { ...state, payCycle: action.payload };
    case 'SET_HOURS_PER_CYCLE':
      return { ...state, hoursPerCycle: action.payload };
    case 'SET_ADDITION_TO_BASE':
      return { ...state, additionToBase: action.payload };
    case 'SET_ADDITION_TO_TOTAL':
      return { ...state, additionToTotal: action.payload };
    case 'SET_BREAKDOWNS':
      return { ...state, breakdowns: action.payload };
    case 'SET_CURRENCY':
      return { ...state, currency: action.payload };
    case 'SET_SALARY':
      return { ...state, salary: action.payload };
    case 'SET_HAS_END_DATE':
      return { ...state, hasEndDate: action.payload };
    case 'SET_SAME_AS_COMPANY_START_DATE':
      return { ...state, sameAsCompanyStartDate: action.payload };
    case 'SET_NO_SUPERVISOR':
      return { ...state, noSupervisor: action.payload };
    case 'SET_NO_FTE':
      return { ...state, noFTE: action.payload };
    case 'SET_REMUNERATION_SCHEDULE_TYPE':
      return { ...state, remunerationScheduleType: action.payload };
    case 'SET_PROBATION_IS_NOT_APPLICABLE':
      return { ...state, probationIsNotApplicable: action.payload };
    case 'SET_USE_DEFAULT_PROBATION_LENGTH':
      return { ...state, useDefaultProbationLength: action.payload };
    default:
      return state;
  }
}
